import type { TFunction } from "i18next";

export const PAYMENT_METHODS = [
  "credit_card",
  "paypal",
  "overpay",
  "stripe",
] as const;

export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_FEE_KEYS = {
  credit_card: "creditCard",
  paypal: "paypal",
  overpay: "overpay",
  stripe: "stripe",
} as const satisfies Record<PaymentMethod, string>;

export type PaymentFeeKey = (typeof PAYMENT_METHOD_FEE_KEYS)[PaymentMethod];

export const getPaymentMethodLabel = (t: TFunction, method: PaymentMethod) => {
  switch (method) {
    case "credit_card":
      return t("common.creditCard");
    case "paypal":
      return "PayPal";
    case "overpay":
      return t("common.overpay");
    case "stripe":
      return t("common.stripe");
  }
};

export const isPaymentMethod = (value: unknown): value is PaymentMethod =>
  typeof value === "string" && PAYMENT_METHODS.includes(value as PaymentMethod);

// USDT deposits top up the balance instead of charging a provider, so the
// backend never lists them in `payment_methods` and they carry no
// `fee_percent` key. The Outgoing selector offers them as an extra option next
// to the real methods; everything fee-related keeps working on PaymentMethod.
export const USDT_PAYMENT_OPTION = "usdt" as const;

export const USDT_PAYMENT_LABEL = "USDT (TRC-20)";

export type PaymentOption = PaymentMethod | typeof USDT_PAYMENT_OPTION;

export const isPaymentOption = (value: unknown): value is PaymentOption =>
  value === USDT_PAYMENT_OPTION || isPaymentMethod(value);

export const toPaymentMethod = (
  option: PaymentOption | undefined,
): PaymentMethod | undefined => (isPaymentMethod(option) ? option : undefined);

export const getPaymentOptionLabel = (t: TFunction, option: PaymentOption) =>
  option === USDT_PAYMENT_OPTION
    ? USDT_PAYMENT_LABEL
    : getPaymentMethodLabel(t, option);
